import type { UserRole } from '@/hooks/useCurrentUser';

/**
 * Espejo de RoleGrantPolicy del backend (PMP.SharedContracts/Authorization/RoleGrantPolicy.cs).
 * Sirve solo para que la UI no ofrezca roles que el servidor va a rechazar con 403. La
 * autoridad es el backend; si las dos matrices divergen, la que manda es la de C#.
 */
const ALL_ROLES: readonly UserRole[] = [
  'Employee', 'AdminOU', 'AdminGlobal', 'AdminPulso', 'AdminBrillamos', 'AdminOUBrillamos',
];

const PULSO_ROLES: readonly UserRole[] = ['Employee', 'AdminOU', 'AdminPulso'];

const BRILLAMOS_ROLES: readonly UserRole[] = ['Employee', 'AdminBrillamos', 'AdminOUBrillamos'];

/** Admin OU Reconocimiento (2026-08-21, espejo de RoleGrantPolicy.GrantableBy en el backend): solo
 *  otorga su propio rol — nunca AdminBrillamos/AdminGlobal. Es aditivo: si el actor combina este rol
 *  con uno tenant-wide, esa rama ya cubre lo mismo o más, así que unir aquí no reduce nada. */
const OU_RECONOCIMIENTO_ROLES: readonly UserRole[] = ['Employee', 'AdminOUBrillamos'];

/** Roles que el actor puede asignar, según los roles que él mismo tiene. */
export function grantableRoles(actorRoles: readonly UserRole[]): UserRole[] {
  if (actorRoles.includes('AdminGlobal')) return [...ALL_ROLES];

  const grantable = new Set<UserRole>();
  if (actorRoles.includes('AdminPulso')) PULSO_ROLES.forEach(role => grantable.add(role));
  if (actorRoles.includes('AdminBrillamos')) BRILLAMOS_ROLES.forEach(role => grantable.add(role));
  if (actorRoles.includes('AdminOUBrillamos')) OU_RECONOCIMIENTO_ROLES.forEach(role => grantable.add(role));
  return [...grantable];
}

/** True si el actor puede modificar al usuario destino sin salirse de su ámbito de delegación. */
export function canManageUser(
  actorRoles: readonly UserRole[],
  targetRoles: readonly UserRole[],
): boolean {
  const grantable = grantableRoles(actorRoles);
  if (grantable.length === 0) return false;
  if (targetRoles.length === 0) return grantable.includes('Employee');
  return targetRoles.every(role => grantable.includes(role));
}
